import Toast, { ToastProps, ToastVariants } from '@features/ui-atoms/components/Toast';
import { Id, toast, ToastOptions } from 'react-toastify';

/**
 * Show a toast notification
 * @param param0
 * @param {number} param0.autoClose Time in milliseconds before the toast is automatically closed
 * @param {() => void} param0.onClose Callback when the toast is closed
 * @param {ToastVariants} param0.variant The variant of the toast
 * @param {string} param0.title Title of the toast
 * @param {string} param0.message Message of the toast (if children is not provided)
 * @param {Id} param0.toastId Identity of the toast — same id replaces, different ids stack (defaults to the title)
 * @param {ReactNode} param0.children Children of the toast (if message is not provided)
 * @returns void
 */
export function showToast({
  onClose,
  autoClose,
  toastId,
  variant = ToastVariants.INFO,
  ...props
}: ToastProps & Pick<ToastOptions, 'autoClose' | 'toastId'>): Id {
  return toast(({ closeToast }) => <Toast {...props} variant={variant} closeToast={closeToast} />, {
    autoClose,
    closeButton: false,
    hideProgressBar: false,
    icon: false,
    position: 'bottom-right',
    // Defaults to the title, which collapses unrelated notifications that happen to
    // share one — pass an explicit id when several toasts must coexist.
    toastId: toastId ?? props.title,
    type: variantToType[variant],

    // onClose is not triggered by the Toast component itself but here to catch all possible ways of closing the toast
    onClose,

    bodyStyle: {
      margin: 0,
      borderRadius: 0,
      padding: 0,
    },

    style: {
      backgroundColor: 'transparent',
      borderRadius: 0,
      minHeight: 0,
      padding: 0,
    },

    // The toast progress bar color is controlled by CSS vars located in global.css
  });
}

type VariantToType = {
  [variant in ToastVariants]: ToastOptions['type'];
};
const variantToType: VariantToType = {
  [ToastVariants.ERROR]: 'error',
  [ToastVariants.INFO]: 'info',
  [ToastVariants.SUCCESS]: 'success',
  [ToastVariants.WARNING]: 'warning',
};
